import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { BulkTranslationLanguageSelector } from './BulkTranslationLanguageSelector'
import { CategoryMultiSelector } from './CategoryMultiSelector'

interface BulkTranslationModalProps {
  categories: Array<{ id: string; name: string }>
  onTranslate: (sourceLanguage: string, targetLanguages: string[], categories: string[], maxConcurrency?: number) => void
  onClose: () => void
}

export function BulkTranslationModal({ categories, onTranslate, onClose }: BulkTranslationModalProps) {
  const { t } = useTranslation()
  const [sourceLanguage, setSourceLanguage] = useState('en')
  const [targetLanguages, setTargetLanguages] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categories.map(c => c.id))
  
  // Default DeepSeek configuration values (hidden from user)
  // const useDeepSeekConfig = true
  // const temperature = 1.3
  const maxConcurrency = 20
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  const availableTargetLanguages = SUPPORTED_LANGUAGES.filter(lang => lang.code !== sourceLanguage)

  const handleTargetLanguageToggle = (languageCode: string) => {
    setTargetLanguages(prev => 
      prev.includes(languageCode)
        ? prev.filter(code => code !== languageCode)
        : [...prev, languageCode]
    )
  }

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleTranslate = () => {
    if (targetLanguages.length === 0) {
      toast.error(t('translations.selectTargetLanguage'))
      return
    }

    if (selectedCategories.length === 0) {
      toast.error(t('translations.selectCategory'))
      return
    }

    const totalTranslations = selectedCategories.length * targetLanguages.length
    const estimatedTime = Math.ceil(totalTranslations * 2) // 2 seconds per translation

    toast.success(
      t('translations.startingBulkTranslation', { 
        categories: selectedCategories.length, 
        languages: targetLanguages.length, 
        time: estimatedTime 
      })
    )

    onTranslate(sourceLanguage, targetLanguages, selectedCategories, maxConcurrency)
  }

  const selectAllTargets = () => {
    setTargetLanguages(availableTargetLanguages.map(lang => lang.code))
  }

  const clearAllTargets = () => {
    setTargetLanguages([])
  }

  const selectAllCategories = () => {
    setSelectedCategories(categories.map(c => c.id))
  }

  const clearAllCategories = () => {
    setSelectedCategories([])
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🚀</span>
              {t('actions.bulkTranslate')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Language Selection */}
          <BulkTranslationLanguageSelector
            sourceLanguage={sourceLanguage}
            targetLanguages={targetLanguages}
            languages={SUPPORTED_LANGUAGES}
            onSourceLanguageChange={setSourceLanguage}
            onTargetLanguageToggle={handleTargetLanguageToggle}
            onSelectAllTargets={selectAllTargets}
            onClearAllTargets={clearAllTargets}
          />

          {/* Categories */}
          <CategoryMultiSelector
            label={t('categories.title')}
            selectedCategories={selectedCategories}
            onToggleCategory={handleCategoryToggle}
            onSelectAll={selectAllCategories}
            onClearAll={clearAllCategories}
            categories={categories}
            selectAllText={t('actions.selectAll')}
            clearAllText={t('actions.clearAll')}
          />

        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleTranslate}
              disabled={targetLanguages.length === 0 || selectedCategories.length === 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center"
            >
              <span className="mr-2">🚀</span>
              {t('translations.startBulkTranslation')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 